import {adjustFromTimeZoneSafely, getTransportCategory, TransportCategory} from "@ots-frontend/ots-data";
import type {ActivityTicketDraft, ActivityTransportDraft} from "@ots-frontend/ots-ui-tw";

/** Where a prefilled ticket opens and what the form's route state carries. */
export type TicketFormNavigation = {
  path: string;
  state: Record<string, unknown>;
};

const withoutUndefined = <T extends object>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined)) as T;

const transportDefaults = (draft: ActivityTicketDraft, side: ActivityTransportDraft | undefined, category: TransportCategory | undefined) =>
  withoutUndefined({
    // The forms show and take times on the app time zone's wall clock.
    date: adjustFromTimeZoneSafely(side?.time),
    person: draft.personId,
    vehicle: draft.vehicleId,
    trailer: draft.trailerId,
    siteCompany: side?.siteCompanyId,
    site: side?.siteId,
    container: side?.containerId,
    category,
  });

/**
 * DispatchGo's form for a ticket drafted from Driver Activity: the ticket form for a pickup and delivery, the
 * transport form for one side alone, each prefilled with the draft's driver, equipment, times, and sites.
 */
export const ticketFormNavigation = (draft: ActivityTicketDraft): TicketFormNavigation => {
  const category = getTransportCategory(draft.category);
  const options = withoutUndefined({regionId: draft.regionId, category});
  if (draft.kind === "ticket") {
    return {
      path: "/tickets/form",
      state: {
        formDefaults: {
          pickup: transportDefaults(draft, draft.pickup, category),
          delivery: transportDefaults(draft, draft.delivery, category),
        },
        options,
      },
    };
  }
  return {
    path: "/transports/form",
    state: {
      type: draft.kind === "pickup" ? "PICKUP" : "DELIVERY",
      formDefaults: transportDefaults(draft, draft.kind === "pickup" ? draft.pickup : draft.delivery, category),
      options,
    },
  };
};
